import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  forgotPasswordSchema,
  TForgotPasswordSchema,
} from '@/schemas/auth/forgot-password.schema';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSendEmailRestPassword } from '@/hooks/auth.hooks';
import { AuthLayout } from '@/layouts/AuthLayout';
import { ForgotPasswordManagement } from '../components/ForgotPasswordManagement';
import { Loader } from '@/components/Loading/Loader';
import { ForgotPasswordEmailReceipt } from '../components/ForgotPasswordEmailReceipt';
import forgotPasswordCarIllusationUrl from '@/assets/svg/forgot-password-car.svg';
import { twMerge } from 'tailwind-merge';
import { getAccessToken } from '@/utils/token.utils';

export const ForgotPassword = () => {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const [responseError, setResponseError] = useState('');
  const [email, setEmail] = useState('');

  useEffect(() => {
    if (getAccessToken()) {
      navigate('/');
    }
  }, [navigate]);

  const {
    control,
    register,
    setValue,
    watch,
    setError,
    handleSubmit,
    formState: { errors },
  } = useForm<TForgotPasswordSchema>({
    resolver: zodResolver(forgotPasswordSchema),
  });

  const { mutate, isPending } = useSendEmailRestPassword({
    handleSuccess: (data) => {
      setEmail(data?.['data']?.email);
    },
    handleError: (error) => {
      if (error?.status === 404 || error?.error === 'Not Found') {
        setResponseError(t('emailNotAssociatedWithAnyAccount'));
      } else {
        setResponseError(t('common:errorOccurredTryAgainLater'));
      }
    },
  });

  const onSubmit = (email: TForgotPasswordSchema) => {
    mutate(email);
  };

  return (
    <>
      <AuthLayout
        contentContainerClassName={twMerge(
          '',
          email && 'max-3xl:mt-0 min-3xl:mt-[60px]',
        )}
        illustrationUrl={forgotPasswordCarIllusationUrl}
        title={!email && `${t('common:forgotPassword')} ?`}
        description={!email && t('enterYourEmailToResetPassword')}
      >
        {email ? (
          <ForgotPasswordEmailReceipt t={t} email={email} setEmail={setEmail} />
        ) : (
          <ForgotPasswordManagement<TForgotPasswordSchema>
            t={t}
            responseError={responseError}
            setResponseError={setResponseError}
            control={control}
            register={register}
            setValue={setValue}
            watch={watch}
            setError={setError}
            errors={errors}
            submitDisabled={isPending}
            onSubmit={handleSubmit(onSubmit)}
          />
        )}
      </AuthLayout>
      {isPending && <Loader />}
    </>
  );
};
